/**
 * MSAL Provider for Microsoft Graph API Authentication
 *
 * This provider uses MSAL.js to acquire tokens for Microsoft Graph API.
 * It works alongside the existing Cognito-based Microsoft SSO authentication.
 *
 * Prerequisites in Azure Portal:
 * 1. Add SPA redirect URI: http://localhost:5173 (dev) and production URLs
 * 2. Add API permissions: Microsoft Graph > Calendars.Read, Calendars.ReadWrite, User.Read
 * 3. Grant admin consent for the permissions (or users will be prompted)
 */

import {
  PublicClientApplication,
  Configuration,
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
  SilentRequest,
  PopupRequest,
} from "@azure/msal-browser";
import { compassusMicrosoftTenantId } from "../../../../config";

// Microsoft Entra ID App Registration details
// From: https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/Overview/appId/e87b21cd-76ca-4eb8-a276-83875581055c
const MICROSOFT_CLIENT_ID = "e87b21cd-76ca-4eb8-a276-83875581055c";

// Authority options:
// - "common" = multitenant (any Azure AD + personal Microsoft accounts)
// - "organizations" = any Azure AD account (work/school only)
// - "consumers" = personal Microsoft accounts only
// - "{tenant-id}" = single tenant (your org only)
//
// Using Circadia's tenant ID for single-tenant authentication

const TENANT_ID = compassusMicrosoftTenantId;

// Warn if tenant ID is not configured - single-tenant apps REQUIRE a tenant ID
if (!TENANT_ID) {
  console.error(
    "[MSAL] ⚠️ VITE_COMPASSUS_MICROSOFT_TENANT_ID is not set!",
    "Single-tenant Azure AD apps cannot use /common endpoint.",
    "Please set the tenant ID in your .env file."
  );
}

const MICROSOFT_AUTHORITY = TENANT_ID
  ? `https://login.microsoftonline.com/${TENANT_ID}`
  : "https://login.microsoftonline.com/common";

// Scopes needed for Microsoft Graph API
export const GRAPH_SCOPES = {
  // Basic user profile
  USER_READ: "User.Read",
  // Calendar permissions
  CALENDARS_READ: "Calendars.Read",
  CALENDARS_READ_WRITE: "Calendars.ReadWrite",
  // OpenID Connect scopes (automatically included)
  OPENID: "openid",
  PROFILE: "profile",
  EMAIL: "email",
  OFFLINE_ACCESS: "offline_access",
};

// Default scopes for calendar access
export const DEFAULT_CALENDAR_SCOPES = [
  GRAPH_SCOPES.USER_READ,
  GRAPH_SCOPES.CALENDARS_READ,
  GRAPH_SCOPES.CALENDARS_READ_WRITE,
];

// MSAL Configuration
const msalConfig: Configuration = {
  auth: {
    clientId: MICROSOFT_CLIENT_ID,
    authority: MICROSOFT_AUTHORITY,
    // Redirect URI must match what's configured in Azure Portal
    // For SPA, use the app's origin
    redirectUri: window.location.origin,
  },
  cache: {
    // Use localStorage for persistence across browser sessions
    cacheLocation: "localStorage",
  },
  system: {
    // Log level for debugging
    loggerOptions: {
      logLevel: 3, // Info level
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case 0: // Error
            console.error("[MSAL]", message);
            break;
          case 1: // Warning
            console.warn("[MSAL]", message);
            break;
          case 2: // Info
            // Info level - only log in development
            break;
          case 3: // Verbose
            // Verbose level - disabled
            break;
        }
      },
    },
  },
};

// Singleton MSAL instance
let msalInstance: PublicClientApplication | null = null;
let msalInitialized = false;
let initializationPromise: Promise<void> | null = null;

/**
 * Initialize MSAL instance
 * Call this early in the app lifecycle
 */
export const initializeMsal = async (): Promise<void> => {
  if (msalInitialized) return;

  if (initializationPromise) {
    return initializationPromise;
  }

  initializationPromise = (async () => {
    try {
      // Debug: Log current URL to see if we have auth response in hash
      const hasAuthResponse =
        window.location.hash.includes("code=") ||
        window.location.hash.includes("access_token=") ||
        window.location.hash.includes("error=");
      if (hasAuthResponse) {
        // eslint-disable-next-line no-console
        console.log("[MSAL] 🔍 Auth response detected in URL hash");
      }

      msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();

      // Handle redirect response if coming back from a redirect flow
      // Wrap in try-catch to handle stale cache errors gracefully
      try {
        // eslint-disable-next-line no-console
        console.log("[MSAL] Checking for redirect response...");
        const redirectResponse = await msalInstance.handleRedirectPromise();

        // If we got a response from redirect, store the token
        if (redirectResponse) {
          // eslint-disable-next-line no-console
          console.log(
            "[MSAL] ✅ Redirect login completed:",
            redirectResponse.account?.username
          );
          localStorage.setItem("msAccessToken", redirectResponse.accessToken);
          localStorage.removeItem("msalRedirectPending");
        } else {
          // eslint-disable-next-line no-console
          console.log(
            "[MSAL] No redirect response (normal if not returning from login)"
          );
        }
      } catch (redirectError: unknown) {
        // If there's a stale cache error, clear it and continue
        const errorMessage =
          redirectError instanceof Error
            ? redirectError.message
            : String(redirectError);
        if (errorMessage.includes("no_token_request_cache_error")) {
          console.warn(
            "[MSAL] Clearing stale cache from previous incomplete auth flow"
          );
          clearMsalCache();
        } else {
          console.error("[MSAL] Redirect promise error:", redirectError);
        }
        localStorage.removeItem("msalRedirectPending");
      }

      msalInitialized = true;

      // Log accounts after initialization
      const accounts = msalInstance.getAllAccounts();
      // eslint-disable-next-line no-console
      console.log(
        "[MSAL] Initialized. Accounts found:",
        accounts.length,
        accounts.map((a) => a.username)
      );
    } catch (error) {
      console.error("[MSAL] Initialization failed:", error);
      // Reset state so it can be retried
      initializationPromise = null;
      throw error;
    }
  })();

  return initializationPromise;
};

/**
 * Get the MSAL instance
 */
export const getMsalInstance = (): PublicClientApplication | null => {
  return msalInstance;
};

/**
 * Check if MSAL is initialized
 */
export const isMsalInitialized = (): boolean => {
  return msalInitialized;
};

/**
 * Get all accounts known to MSAL
 */
export const getAllAccounts = (): AccountInfo[] => {
  if (!msalInstance) return [];
  return msalInstance.getAllAccounts();
};

/**
 * Get the active/current account
 * Prefers an account that matches the user's email from Cognito SSO
 */
export const getActiveAccount = (
  preferredEmail?: string
): AccountInfo | null => {
  if (!msalInstance) return null;

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  // If we have a preferred email (from Cognito), try to match it
  if (preferredEmail) {
    const matchingAccount = accounts.find(
      (acc) =>
        acc.username.toLowerCase() === preferredEmail.toLowerCase() ||
        acc.idTokenClaims?.email?.toString().toLowerCase() ===
          preferredEmail.toLowerCase()
    );
    if (matchingAccount) return matchingAccount;
  }

  // Otherwise return the first account
  return accounts[0];
};

/**
 * Acquire token silently (no user interaction)
 * This works if the user has already consented to the scopes
 */
export const acquireTokenSilent = async (
  scopes: string[] = DEFAULT_CALENDAR_SCOPES,
  account?: AccountInfo
): Promise<AuthenticationResult | null> => {
  if (!msalInstance || !msalInitialized) {
    console.error("[MSAL] Not initialized. Call initializeMsal() first.");
    return null;
  }

  const activeAccount = account || getActiveAccount();
  if (!activeAccount) {
    return null;
  }

  const silentRequest: SilentRequest = {
    scopes,
    account: activeAccount,
    forceRefresh: false,
  };

  try {
    const response = await msalInstance.acquireTokenSilent(silentRequest);
    return response;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Silent acquisition failed, interaction required
      return null;
    }
    console.error("[MSAL] Silent token acquisition error:", error);
    throw error;
  }
};

/**
 * Login with popup (for first-time authentication)
 * This establishes the user session with Microsoft
 */
export const loginPopup = async (
  scopes: string[] = DEFAULT_CALENDAR_SCOPES
): Promise<AuthenticationResult | null> => {
  if (!msalInstance || !msalInitialized) {
    console.error("[MSAL] Not initialized. Call initializeMsal() first.");
    return null;
  }

  const loginRequest: PopupRequest = {
    scopes,
    prompt: "select_account", // Force account selection
  };

  try {
    // eslint-disable-next-line no-console
    console.log("[MSAL] Opening login popup...");
    const response = await msalInstance.loginPopup(loginRequest);
    // eslint-disable-next-line no-console
    console.log("[MSAL] Login successful:", response.account?.username);
    return response;
  } catch (error) {
    console.error("[MSAL] Login popup error:", error);
    throw error;
  }
};

/**
 * Login with redirect (alternative to popup)
 * Use this if popups are blocked or converted to tabs
 * After redirect, call handleRedirectAndGetToken() to complete
 */
export const loginRedirect = async (
  scopes: string[] = DEFAULT_CALENDAR_SCOPES
): Promise<void> => {
  // eslint-disable-next-line no-console
  console.log("[MSAL] loginRedirect() called");
  // eslint-disable-next-line no-console
  console.log("[MSAL] Current URL:", window.location.href);
  // eslint-disable-next-line no-console
  console.log("[MSAL] Redirect URI configured:", window.location.origin);
  // eslint-disable-next-line no-console
  console.log("[MSAL] Scopes requested:", scopes);

  if (!msalInstance || !msalInitialized) {
    // eslint-disable-next-line no-console
    console.log("[MSAL] Initializing MSAL first...");
    await initializeMsal();
  }

  // Store that we're doing a redirect login so we can handle it on return
  localStorage.setItem("msalRedirectPending", "true");
  // eslint-disable-next-line no-console
  console.log("[MSAL] Set msalRedirectPending=true in localStorage");

  // eslint-disable-next-line no-console
  console.log("[MSAL] 🚀 Redirecting to Microsoft login NOW...");
  // eslint-disable-next-line no-console
  console.log(
    "[MSAL] (This page will navigate away. When you return, check the console for redirect response logs)"
  );

  await msalInstance!.loginRedirect({
    scopes,
    prompt: "select_account",
  });
};

/**
 * Handle redirect response after returning from Microsoft login
 * Call this on app initialization to complete redirect flow
 */
export const handleRedirectAndGetToken =
  async (): Promise<AuthenticationResult | null> => {
    if (!msalInstance || !msalInitialized) {
      await initializeMsal();
    }

    try {
      const response = await msalInstance!.handleRedirectPromise();

      if (response) {
        // eslint-disable-next-line no-console
        console.log(
          "[MSAL] Redirect login successful:",
          response.account?.username
        );
        localStorage.setItem("msAccessToken", response.accessToken);
        localStorage.removeItem("msalRedirectPending");
        return response;
      }

      // No redirect response, check if we were expecting one
      if (localStorage.getItem("msalRedirectPending") === "true") {
        console.warn("[MSAL] Redirect was pending but no response received");
        localStorage.removeItem("msalRedirectPending");
      }

      return null;
    } catch (error) {
      console.error("[MSAL] Handle redirect error:", error);
      localStorage.removeItem("msalRedirectPending");
      throw error;
    }
  };

/**
 * Acquire token with popup (user interaction)
 * Use this when silent acquisition fails but user is already logged in
 */
export const acquireTokenPopup = async (
  scopes: string[] = DEFAULT_CALENDAR_SCOPES
): Promise<AuthenticationResult | null> => {
  if (!msalInstance || !msalInitialized) {
    console.error("[MSAL] Not initialized. Call initializeMsal() first.");
    return null;
  }

  const popupRequest: PopupRequest = {
    scopes,
    prompt: "select_account", // Force account selection
  };

  try {
    const response = await msalInstance.acquireTokenPopup(popupRequest);
    return response;
  } catch (error) {
    console.error("[MSAL] Popup token acquisition error:", error);
    throw error;
  }
};

/**
 * Login with popup and acquire token
 * This is the main entry point for getting a Microsoft Graph token
 */
export const loginAndGetToken = async (
  scopes: string[] = DEFAULT_CALENDAR_SCOPES
): Promise<AuthenticationResult | null> => {
  if (!msalInstance || !msalInitialized) {
    await initializeMsal();
  }

  const accounts = getAllAccounts();

  // If we have accounts, try silent acquisition first
  if (accounts.length > 0) {
    try {
      const silentResult = await acquireTokenSilent(scopes, accounts[0]);
      if (silentResult) {
        // Store token in localStorage for microsoftGraphApi.ts to use
        localStorage.setItem("msAccessToken", silentResult.accessToken);
        // eslint-disable-next-line no-console
        console.log(
          "[MSAL] Token acquired silently and stored in localStorage"
        );
        return silentResult;
      }
    } catch {
      // Silent failed, will try interactive
    }

    // Silent failed, try acquireTokenPopup (user already has account)
    try {
      const popupResult = await acquireTokenPopup(scopes);
      if (popupResult) {
        localStorage.setItem("msAccessToken", popupResult.accessToken);
        // eslint-disable-next-line no-console
        console.log(
          "[MSAL] Token acquired via acquireTokenPopup and stored in localStorage"
        );
        return popupResult;
      }
    } catch {
      // Popup failed too
    }
  }

  // No accounts - need to do a full login first
  // eslint-disable-next-line no-console
  console.log("[MSAL] No existing accounts, initiating login...");
  const loginResult = await loginPopup(scopes);
  if (loginResult) {
    // Store token in localStorage for microsoftGraphApi.ts to use
    localStorage.setItem("msAccessToken", loginResult.accessToken);
    // eslint-disable-next-line no-console
    console.log(
      "[MSAL] Token acquired via loginPopup and stored in localStorage"
    );
  }
  return loginResult;
};

/**
 * Get a valid access token for Microsoft Graph API
 * This is the main function to call when you need to make Graph API calls
 *
 * Returns the access token string, or null if unable to acquire
 */
export const getGraphAccessToken = async (
  scopes: string[] = DEFAULT_CALENDAR_SCOPES
): Promise<string | null> => {
  const result = await loginAndGetToken(scopes);
  return result?.accessToken || null;
};

/**
 * Logout from Microsoft (MSAL)
 * Note: This doesn't affect Cognito SSO - only the Microsoft Graph token
 */
export const msalLogout = async (): Promise<void> => {
  if (!msalInstance) return;

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return;

  try {
    // Use popup logout to avoid full page redirect
    await msalInstance.logoutPopup({
      account: accounts[0],
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (error) {
    console.error("[MSAL] Logout error:", error);
  }
};

/**
 * Clear MSAL cache
 */
export const clearMsalCache = (): void => {
  // Clear MSAL-related items from localStorage
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith("msal.") || key.includes(MICROSOFT_CLIENT_ID))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

/**
 * Reset MSAL state completely
 * Use this when encountering cache errors
 */
export const resetMsal = (): void => {
  // eslint-disable-next-line no-console
  console.log("[MSAL] Resetting MSAL state...");
  clearMsalCache();
  localStorage.removeItem("msAccessToken");
  msalInstance = null;
  msalInitialized = false;
  initializationPromise = null;
  // eslint-disable-next-line no-console
  console.log(
    "[MSAL] Reset complete. Run window.msal.quickTest() to re-authenticate."
  );
};

/**
 * Quick test function for console debugging
 * Initializes MSAL, acquires token, stores it, and tests the connection
 */
export const quickTest = async (): Promise<void> => {
  // eslint-disable-next-line no-console
  console.log("[MSAL] Starting quick test...");

  try {
    // Initialize if needed
    if (!msalInitialized) {
      // eslint-disable-next-line no-console
      console.log("[MSAL] Initializing...");
      await initializeMsal();
    }

    // Get token (will store automatically)
    // eslint-disable-next-line no-console
    console.log("[MSAL] Acquiring token...");
    const result = await loginAndGetToken();

    if (result) {
      // eslint-disable-next-line no-console
      console.log("[MSAL] ✅ Token acquired successfully!");
      // eslint-disable-next-line no-console
      console.log("[MSAL] Account:", result.account?.username);
      // eslint-disable-next-line no-console
      console.log("[MSAL] Token expires:", result.expiresOn);
      // eslint-disable-next-line no-console
      console.log("[MSAL] Scopes:", result.scopes);
      // eslint-disable-next-line no-console
      console.log("");
      // eslint-disable-next-line no-console
      console.log("Now you can test Microsoft Graph API:");
      // eslint-disable-next-line no-console
      console.log("  await window.microsoftGraph.getUserProfile()");
      // eslint-disable-next-line no-console
      console.log("  await window.microsoftGraph.getUserCalendars()");
      // eslint-disable-next-line no-console
      console.log("  await window.microsoftGraph.testConnection()");
    } else {
      console.error("[MSAL] ❌ Failed to acquire token");
    }
  } catch (error: unknown) {
    console.error("[MSAL] ❌ Error:", error);

    // If it's a cache error, suggest reset
    const errorMessage = error instanceof Error ? error.message : String(error);
    if (
      errorMessage.includes("cache") ||
      errorMessage.includes("token_request")
    ) {
      // eslint-disable-next-line no-console
      console.log("");
      // eslint-disable-next-line no-console
      console.log("[MSAL] This looks like a cache error. Try running:");
      // eslint-disable-next-line no-console
      console.log("  window.msal.reset()");
      // eslint-disable-next-line no-console
      console.log("  await window.msal.quickTest()");
    }
  }
};

// Export for debugging in console
if (typeof window !== "undefined") {
  (window as any).msal = {
    initialize: initializeMsal,
    getInstance: getMsalInstance,
    getAllAccounts,
    getActiveAccount,
    acquireTokenSilent,
    acquireTokenPopup,
    loginPopup,
    loginRedirect,
    handleRedirectAndGetToken,
    loginAndGetToken,
    getGraphAccessToken,
    logout: msalLogout,
    clearCache: clearMsalCache,
    reset: resetMsal,
    quickTest,
    SCOPES: GRAPH_SCOPES,
    // Helper to explain usage
    help: () => {
      /* eslint-disable no-console */
      console.log("=== MSAL Console Commands ===");
      console.log("");
      console.log(
        "If popups are blocked (opening as tabs), use REDIRECT flow:"
      );
      console.log(
        "  1. await window.msal.loginRedirect()  // Redirects to Microsoft"
      );
      console.log("  2. After sign-in, you'll return here automatically");
      console.log("  3. Token will be stored automatically");
      console.log("");
      console.log("If popups work, use POPUP flow:");
      console.log("  await window.msal.quickTest()");
      console.log("");
      console.log("To check current state:");
      console.log("  window.microsoftGraph.debugAuthState()");
      console.log("");
      console.log("To reset and start fresh:");
      console.log("  window.msal.reset()");
      console.log("================================");
      /* eslint-enable no-console */
    },
  };

  // Auto-initialize on page load to capture redirect responses
  // Only if user is in a Microsoft SSO session
  const isMicrosoftSso = localStorage.getItem("microsoftSso") === "true";
  const hasRedirectPending =
    localStorage.getItem("msalRedirectPending") === "true";
  const hasAuthInHash =
    window.location.hash.includes("code=") ||
    window.location.hash.includes("access_token=") ||
    window.location.hash.includes("id_token=");

  if (isMicrosoftSso || hasRedirectPending || hasAuthInHash) {
    // eslint-disable-next-line no-console
    console.log("[MSAL] Auto-initializing on page load...", {
      isMicrosoftSso,
      hasRedirectPending,
      hasAuthInHash,
      hash: hasAuthInHash ? "(auth response in hash)" : "(no auth in hash)",
    });

    initializeMsal()
      .then(() => {
        // eslint-disable-next-line no-console
        console.log("[MSAL] Auto-initialization complete");
      })
      .catch((err) => {
        console.error("[MSAL] Auto-initialization failed:", err);
      });
  }
}
